import React, { FC, useState, useRef, FormEvent } from 'react'
/** @jsx jsx */
import { jsx, css } from '@emotion/core'
import { Button } from '@components/Button'
import { MiniPopup } from '@components/Popup'
import { LeadgenPopupType } from '@components/LeadgenPopupFlow'
import { validateQuestionInput } from '@lib/validators'
import { Question } from '@components/Question'
import {
  Title,
  Form,
  List,
  ListItem,
  ErrorText,
  updateButtonStyles,
} from './PreLeadPopup.elements'
import { getInputsFromData } from './get-inputs'

type ErrorType = {
  key: string
  text: string
  answer: string
}

type Props = {
  variant: LeadgenPopupType
  onClose: () => void
  onSubmit: (answers: Record<string, any>) => void
  address_zip: string
  address_state: string
}

export const PreLeadPopup: FC<Props> = props => {
  const {
    onClose,
    onSubmit,
    variant,
    address_state,
    address_zip,
  } = props

  const [fields, setFields] = useState<Record<string, any>>({
    address_zip,
    address_state,
  })
  const [inputs] = useState(getInputsFromData(variant))

  const [errors, setErrors] = useState<Record<string, ErrorType | undefined>>({})
  const [showErrors, setShowErrors] = useState<boolean>(false)
  const hasErrors = showErrors && !!Object.keys(errors).find(key => !!errors[key])
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)

  const popupRef = useRef<MiniPopup>(null)

  const handleAnswerQuestion = (name: string, value: any) => {
    const newFields = {
      ...fields,
      [name]: value
    }
    setFields(newFields)

    const error = inputs[name].optional && !value
      ? false
      : validateQuestionInput(inputs[name], value)

    setErrors({
      ...errors,
      [name]: error || undefined
    })

    setShowErrors(false)
  }

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    event.stopPropagation()

    const data = { ...fields }

    const errs = {}

    Object.keys(inputs).forEach(key => {
      const answer = fields[key]
      errs[key] = inputs[key].optional && !answer
        ? false
        : validateQuestionInput(inputs[key], answer)
    })
    setErrors(errs)

    const hasErrors = Object.keys(errs).find(key => !!errs[key])

    if (hasErrors) {
      setShowErrors(true)
    } else {
      setIsSubmitting(true)
      setShowErrors(false)
      onSubmit(data)
    }
  }

  const questionKeys = Object.keys(inputs)

  return (
    <MiniPopup
      pushDown
      onClose={onClose}
      ref={popupRef}
    >
      <Title small children="Edit your information" />

      <Form onSubmit={handleSubmit}>
        <List>
          {questionKeys.map(questionKey => {
            return (
              <ListItem key={`list-item-${questionKey}`}>
                <Question
                  id={`list-item-${questionKey}`}
                  value={fields[questionKey]}
                  error={showErrors && !!errors[questionKey]}
                  inputData={inputs[questionKey]}
                  onAnswerQuestion={handleAnswerQuestion}
                />
              </ListItem>
            )
          })}
        </List>

        <Button
          type="submit"
          css={updateButtonStyles}
          variant="green"
          disabled={isSubmitting}
        >
          {isSubmitting ? 'Loading...' : 'Explore my rates'}
        </Button>
        {hasErrors && (
          <ErrorText>Please check the highlighted fields</ErrorText>
        )}
      </Form>
    </MiniPopup>
  )
}
